import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, ExternalLink, Utensils, Stethoscope, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    service: 'Restaurant & Hospitality', 
    message: '' 
  });
  
  const [status, setStatus] = useState('idle');
  const brandGold = "#ffb700";

  const services = [
    "Restaurant & Hospitality",
    "Healthcare",
    "AI Model Hub",
    "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('https://starkx-email-service.onrender.com/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send request");
      }

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', service: 'Restaurant & Hospitality', message: '' });
      setTimeout(() => setStatus('idle'), 5000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="pt-28 md:pt-32 pb-20 px-4 md:px-12 bg-[#020408] min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-20 mb-20 md:mb-32">
          
          {/* LEFT SIDE: DIRECT CONTACT INFO */}
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-4xl md:text-5xl font-black mb-6 md:mb-8 text-white tracking-tighter">
              Let's <span style={{ color: brandGold }}>Connect.</span>
            </h2>
            <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-12 leading-relaxed">
              Ready to deploy an autonomous receptionist or order engine? 
              Reach out directly or fill out the form to initialize your setup.
            </p>

            <div className="flex flex-col gap-4 md:gap-8 md:block md:space-y-8">
              {/* Email Button (Tap friendly on mobile) */}
              <a 
                href="mailto:info@starkxai.com" 
                className="flex items-center gap-4 md:gap-6 group bg-slate-900/40 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none border border-white/5 md:border-transparent hover:bg-white/5 md:hover:bg-transparent transition-all"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white/5 rounded-2xl flex items-center justify-center group-hover:bg-[#ffb700]/10 transition-colors shrink-0">
                  <Mail className="text-[#ffb700]" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-[10px] md:text-xs uppercase font-black tracking-widest mb-0.5">Email Us</p>
                  <p className="text-white font-bold text-sm md:text-base">info@starkxai.com</p>
                </div>
              </a>

              {/* Phone Button (Tap friendly on mobile) */}
              <a 
                href="tel:+14048201189" 
                className="flex items-center gap-4 md:gap-6 group bg-slate-900/40 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none border border-white/5 md:border-transparent hover:bg-white/5 md:hover:bg-transparent transition-all"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white/5 rounded-2xl flex items-center justify-center group-hover:bg-[#ffb700]/10 transition-colors shrink-0">
                  <Phone className="text-[#ffb700]" size={20} />
                </div>
                <div>
                  <p className="text-slate-500 text-[10px] md:text-xs uppercase font-black tracking-widest mb-0.5">Call Directly</p>
                  <p className="text-white font-bold text-sm md:text-base">+1 (404) 820-1189</p>
                </div>
              </a>
            </div>
          </motion.div>

          {/* RIGHT SIDE: LEAD FORM */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900/50 border border-white/10 p-6 md:p-10 rounded-3xl md:rounded-[3rem] backdrop-blur-xl shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
              
              {/* Name & Phone Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <label className="block text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-2 pl-1">Full Name</label>
                  <input 
                    type="text" required 
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 md:p-4 text-white text-sm md:text-base outline-none focus:border-[#ffb700] transition-colors" 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-2 pl-1">Phone</label>
                  <input 
                    type="tel" required 
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 md:p-4 text-white text-sm md:text-base outline-none focus:border-[#ffb700] transition-colors" 
                    value={formData.phone} 
                    onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                  />
                </div>
              </div>

              {/* Email & Service Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <label className="block text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-2 pl-1">Email Address</label>
                  <input 
                    type="email" required 
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 md:p-4 text-white text-sm md:text-base outline-none focus:border-[#ffb700] transition-colors" 
                    value={formData.email} 
                    onChange={(e) => setFormData({...formData, email: e.target.value})} 
                  />
                </div>
                <div className="relative">
                  <label className="block text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-2 pl-1">Select a Service</label>
                  <div className="relative">
                    <select 
                      required
                      className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 md:p-4 text-white text-sm md:text-base outline-none focus:border-[#ffb700] transition-colors appearance-none cursor-pointer"
                      value={formData.service}
                      onChange={(e) => setFormData({...formData, service: e.target.value})}
                    >
                      {services.map(s => <option key={s} value={s} className="bg-slate-900 text-white">{s}</option>)}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" size={16} />
                  </div>
                </div>
              </div>

              {/* Message Area */}
              <div>
                <label className="block text-[10px] md:text-xs font-black uppercase tracking-widest text-slate-500 mb-2 pl-1">Message (Optional)</label>
                <textarea 
                  rows="3"
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 md:p-4 text-white text-sm md:text-base outline-none focus:border-[#ffb700] transition-colors resize-none" 
                  value={formData.message} 
                  onChange={(e) => setFormData({...formData, message: e.target.value})} 
                />
              </div>

              {/* Submit Button with Status Logic */}
              <button 
                disabled={status === 'sending' || status === 'success'}
                className={`w-full py-4 md:py-5 rounded-xl md:rounded-2xl font-black text-sm md:text-lg flex items-center justify-center gap-3 transition-all mt-2 ${
                  status === 'success' ? 'bg-green-500 text-black' 
                  : status === 'error' ? 'bg-red-500 text-white'
                  : 'bg-[#ffb700] text-black hover:scale-[1.02] shadow-[0_0_20px_rgba(255,183,0,0.2)]'
                }`}
              >
                {status === 'sending' ? (
                  "Transmitting..." 
                ) : status === 'success' ? (
                  <div className="flex flex-col items-center leading-tight">
                    <div className="flex items-center gap-2">
                        <CheckCircle size={18} /> Request Received
                    </div>
                  </div>
                ) : status === 'error' ? (
                  <><AlertCircle size={18} /> Error - Try Again</>
                ) : (
                  <><Send size={18} /> Transmit Inquiry</>
                )}
              </button>
            </form>
          </motion.div>
        </div>

        {/* REDIRECTION SECTION */}
        <div className="border-t border-white/5 pt-16 md:pt-20">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-8 md:mb-10 text-center uppercase tracking-widest opacity-50">
            Explore Our Live Platforms
          </h3>
          <div className="grid md:grid-cols-2 gap-4 md:gap-8">
            <a 
              href="https://starkxrestoservices.onrender.com/" 
              className="group p-5 md:p-8 bg-slate-900/30 border border-white/5 rounded-2xl md:rounded-3xl flex items-center justify-between hover:border-[#ffb700]/50 hover:bg-[#ffb700]/5 transition-all shadow-lg"
            >
              <div className="flex items-center gap-4 md:gap-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-[#ffb700]/10 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                  <Utensils className="text-[#ffb700]" size={24} />
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1">AI for Restaurants</h4>
                  <p className="text-slate-500 text-xs md:text-sm leading-tight">Launch Restaurant POS & Admin Dashboard</p>
                </div>
              </div>
              <ExternalLink className="text-slate-600 group-hover:text-[#ffb700] transition-colors shrink-0 ml-2" size={20} />
            </a>

            <a 
              href="https://starkxaihealth.onrender.com" 
              className="group p-5 md:p-8 bg-slate-900/30 border border-white/5 rounded-2xl md:rounded-3xl flex items-center justify-between hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all shadow-lg"
            >
              <div className="flex items-center gap-4 md:gap-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-cyan-500/10 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                  <Stethoscope className="text-cyan-400" size={24} />
                </div>
                <div>
                  <h4 className="text-lg md:text-xl font-bold text-white mb-1">AI for Healthcare</h4>
                  <p className="text-slate-500 text-xs md:text-sm leading-tight">Launch Patient Portal & Scheduling Hub</p>
                </div>
              </div>
              <ExternalLink className="text-slate-600 group-hover:text-cyan-400 transition-colors shrink-0 ml-2" size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;